import React, { useState, useEffect, useCallback } from "react";
import {
  Select,
  Table,
  Card,
  Input,
  Space,
  Button,
  Modal,
  Dropdown,
  message,
  Typography,
  Row,
  Col,
  Tag,
  Empty,
  Tooltip,
  Skeleton,
  Pagination,
  DatePicker,
} from "antd";
import type { TableProps } from "antd";
import {
  PlusOutlined,
  FileTextOutlined,
  UploadOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
  BookOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import request, { LONG_TASK_TIMEOUT } from "../utils/request";
import { useGlobal, KnowledgeItem } from "../contexts/GlobalContext";
import { useCurrentProject } from "../hooks/useCurrentProject";
import { checkToken } from "../utils/auth";
import { useNavigate } from "react-router-dom";
import KnowledgeEditModal from "../componets/modals/Knowledge/KnowledgeEditModal";
import KnowledgeUploadModal from "../componets/modals/Knowledge/KnowledgeUploadModal";
import KnowledgeTextModal from "../componets/modals/Knowledge/KnowledgeTextModal";
import KnowledgeWebModal from "../componets/modals/Knowledge/KnowledgeWebModal";
import {
  extractSummarySwitchMap,
  normalizePodcastConfig,
} from "../types/podcast";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

/** 与 https://vemetric.com/favicon-api 相同用法，域名置于路径中 */
const FAVICON_API_BASE = "https://ico.tianli0.top";

function extractHostnameForFavicon(record: KnowledgeItem): string | null {
  const raw = record.url || record.refer || record.website;
  if (!raw || typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try {
    if (/^https?:\/\//i.test(trimmed)) {
      return new URL(trimmed).hostname;
    }
    return new URL(`https://${trimmed}`).hostname;
  } catch {
    return null;
  }
}

const IPV4_HOST_RE =
  /^(25[0-5]|2[0-4]\d|[01]?\d\d?)(\.(25[0-5]|2[0-4]\d|[01]?\d\d?)){3}$/;

/** 解析得到的 hostname 是否为 IPv4/IPv6（不请求 favicon API） */
function isIpHostname(hostname: string): boolean {
  const h = hostname.replace(/^\[|\]$/g, "").trim();
  if (IPV4_HOST_RE.test(h)) return true;
  if (h.includes(":")) return true;
  return false;
}

function normalizeKnowledgeItem(
  item: Partial<KnowledgeItem> & { id: number; title: string; created_at: string },
  projectId?: number
): KnowledgeItem {
  return {
    ...item,
    project_id: item.project_id ?? projectId,
    refer: item.refer || item.url || item.website || "",
    content: item.content || "",
    summary: item.summary || "",
    doc_type: item.doc_type || "text",
  };
}

const titleIconBox: React.CSSProperties = {
  width: 20,
  height: 20,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

/** 知识标题前：文件用 Remix 文件图标；URL 为 IP 用 Remix 地球图标；否则站点 favicon */
const TitleSiteFavicon: React.FC<{ record: KnowledgeItem }> = ({
  record,
}) => {
  const docType = record.doc_type || "text";
  const host = React.useMemo(() => extractHostnameForFavicon(record), [record]);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [host]);

  if (docType === "file") {
    return (
      <span style={titleIconBox}>
        <i
          className="ri-file-2-line"
          style={{ fontSize: 18, color: "#1677ff", lineHeight: 1 }}
          aria-hidden
        />
      </span>
    );
  }

  if (!host) {
    return null;
  }

  if (isIpHostname(host) || failed) {
    return (
      <span style={titleIconBox}>
        <i
          className="ri-global-line"
          style={{ fontSize: 18, color: "#52c41a", lineHeight: 1 }}
          aria-hidden
        />
      </span>
    );
  }

  return (
    <img
      src={`${FAVICON_API_BASE}/${host}?size=32`}
      alt=""
      width={20}
      height={20}
      style={{
        borderRadius: 4,
        objectFit: "cover",
        flexShrink: 0,
        display: "block",
      }}
      onError={() => setFailed(true)}
    />
  );
};

const Knowledge: React.FC = () => {
  const { API_BASE_URL, token, projects, user } = useGlobal();
  const { project: routeProject } = useCurrentProject();
  const navigate = useNavigate();
  const [selectedProjectId, setSelectedProjectId] = useState<number | "all">(
    "all"
  );
  const [knowledgeList, setKnowledgeList] = useState<KnowledgeItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const paginationRef = React.useRef(pagination);
  const [searchText, setSearchText] = useState("");
  const [sourceFilter, setSourceFilter] = useState<string | undefined>();
  const [docTypeFilter, setDocTypeFilter] = useState<string | undefined>();
  const [vectorFilter, setVectorFilter] = useState<boolean | undefined>();
  const [dateRangeFilter, setDateRangeFilter] = useState<any>(null);
  // 最近一次“已生效”的搜索关键词（用于分页/改页大小时保持在搜索结果中翻页）
  const [searchedKeyword, setSearchedKeyword] = useState("");
  const searchedKeywordRef = React.useRef("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<KnowledgeItem | null>(
    null
  );
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [isTextModalVisible, setIsTextModalVisible] = useState(false);
  const [isWebModalVisible, setIsWebModalVisible] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [podcastConfig, setPodcastConfig] = useState<any>(null);
  const [podcastWhitelistLoading, setPodcastWhitelistLoading] = useState(false);
  const [togglingSummaryId, setTogglingSummaryId] = useState<number | null>(null);
  const [revectoringId, setRevectoringId] = useState<number | null>(null);
  const [summaryPodcastStatus, setSummaryPodcastStatus] = useState<
    Map<number, boolean>
  >(new Map());

  // 更新 ref 当 pagination 改变时
  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  useEffect(() => {
    searchedKeywordRef.current = searchedKeyword;
  }, [searchedKeyword]);

  // 验证 token
  useEffect(() => {
    if (!token || !checkToken(token)) {
      message.error("登录已过期，请重新登录");
      navigate("/login");
    }
  }, [token, navigate]);

  // 获取播客配置
  const fetchPodcastConfig = useCallback(async () => {
    try {
      const response = await request.get(`${API_BASE_URL}/api/v3/podcasts/config`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.code === 200) {
        setPodcastConfig(normalizePodcastConfig(response.data.data));
      }
    } catch (error) {
      console.error("获取播客配置失败:", error);
    }
  }, [API_BASE_URL, token]);

  // 初始化时获取播客配置
  useEffect(() => {
    if (token) {
      fetchPodcastConfig();
    }
  }, [token, fetchPodcastConfig]);

  // 批量获取摘要的播客开关状态
  const fetchSummaryPodcastStatus = useCallback(
    async (summaryIds: number[]) => {
      if (!token || summaryIds.length === 0) return;

      try {
        const response = await request.get(
          `${API_BASE_URL}/api/v3/podcasts/summary-switches`,
          {
            params: {
              project_id:
                selectedProjectId === "all" ? undefined : selectedProjectId,
            },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.code === 200) {
          setSummaryPodcastStatus(extractSummarySwitchMap(response.data.data));
        }
      } catch (error) {
        console.error("获取摘要播客状态失败:", error);
      }
    },
    [API_BASE_URL, selectedProjectId, token]
  );

  const fetchKnowledgeList = useCallback(
    async (params?: { current?: number; pageSize?: number; keyword?: string }) => {
      if (!token || !checkToken(token)) return;

      const current = params?.current || paginationRef.current.current || 1;
      const pageSize = params?.pageSize || paginationRef.current.pageSize;
      const keyword = (params?.keyword ?? searchedKeywordRef.current).trim();

      setLoading(true);
      try {
        const response = await request.get(`${API_BASE_URL}/api/v3/knowledge/summaries`, {
          params: {
            project_id:
              selectedProjectId === "all" ? undefined : selectedProjectId,
            page: current,
            page_size: pageSize,
            keyword: keyword || undefined,
            refer: sourceFilter || undefined,
            type: docTypeFilter || undefined,
            is_vector:
              typeof vectorFilter === "boolean" ? String(vectorFilter) : undefined,
            start_time: dateRangeFilter?.[0]?.toISOString?.(),
            end_time: dateRangeFilter?.[1]?.toISOString?.(),
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.code !== 200) {
          throw new Error(response.data.msg || "获取知识库列表失败");
        }

        const list = Array.isArray(response.data.data?.list)
          ? response.data.data.list
          : [];
        setKnowledgeList(
          list.map((item: KnowledgeItem) => normalizeKnowledgeItem(item))
        );
        setPagination((prev) => ({
          ...prev,
          current,
          pageSize,
          total: Number(response.data.data?.total || list.length),
        }));
      } catch (error) {
        message.error("获取知识库列表失败");
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    },
    [API_BASE_URL, selectedProjectId, token, sourceFilter, docTypeFilter, vectorFilter, dateRangeFilter]
  );

  useEffect(() => {
    fetchKnowledgeList();
  }, [fetchKnowledgeList]);

  useEffect(() => {
    if (podcastConfig?.specified_mode && knowledgeList.length > 0) {
      const summaryIds = knowledgeList.map((item) => item.id);
      fetchSummaryPodcastStatus(summaryIds);
    }
  }, [podcastConfig?.specified_mode, knowledgeList, fetchSummaryPodcastStatus]);

  // 项目作用域：锁定到路由项目
  useEffect(() => {
    if (routeProject?.id) setSelectedProjectId(routeProject.id);
  }, [routeProject?.id]);

  // 检查是否有项目
  if (projects.length === 0) {
    return (
      <div
        style={{
          background: "#f0f2f5",
          minHeight: "100%",
          borderRadius: 8,
          padding: "16px",
        }}
      >
        <Card>
          <div style={{ textAlign: "center", padding: "32px" }}>
            <BookOutlined
              style={{ fontSize: 48, color: "#1677ff", marginBottom: 16 }}
            />
            <Title level={4}>需要创建一个项目才可使用此功能</Title>
            <Text
              type="secondary"
              style={{ display: "block", marginBottom: 24 }}
            >
              项目是组织和管理知识库的基本单位，请先创建一个项目
            </Text>
            <Button
              type="primary"
              onClick={() => (window.location.href = "/console")}
              size="large"
            >
              返回首页创建项目
            </Button>
          </div>
        </Card>
      </div>
    );
  }


  const applyFilterChange = (
    next: Partial<{
      source: string;
      docType: string;
      vector: boolean;
      dateRange: any;
    }>
  ) => {
    if ("source" in next) setSourceFilter(next.source || undefined);
    if ("docType" in next) setDocTypeFilter(next.docType || undefined);
    if ("vector" in next) setVectorFilter(next.vector);
    if ("dateRange" in next) setDateRangeFilter(next.dateRange || null);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const resetFilters = () => {
    setSourceFilter(undefined);
    setDocTypeFilter(undefined);
    setVectorFilter(undefined);
    setDateRangeFilter(null);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  // 添加搜索函数
  const handleSearch = async () => {
    const keyword = searchText.trim();
    const pageSize = paginationRef.current.pageSize;

    setSearchLoading(true);
    try {
      setSearchedKeyword(keyword);
      await fetchKnowledgeList({ current: 1, pageSize, keyword });
    } finally {
      setSearchLoading(false);
    }
  };

  const handleTableChange = (paginationParams: any) => {
    const current = paginationParams.current;
    const pageSize = paginationParams.pageSize;

    fetchKnowledgeList({ current, pageSize });
  };

  // 获取项目名称
  const getProjectName = (projectId: number) => {
    const project = projects.find((p) => p.id === projectId);
    return project?.name || "-";
  };

  // 修改 handleEdit 函数
  const handleEdit = (record: KnowledgeItem) => {
    setCurrentRecord(record);
    setIsEditModalVisible(true);
  };

  const handleEditModalClose = () => {
    setIsEditModalVisible(false);
    setCurrentRecord(null);
  };

  const handleDelete = (record: KnowledgeItem) => {
    Modal.confirm({
      title: "确认删除",
      content: (
        <div>
          <p>确定要删除以下知识条目吗？</p>
          <p style={{ fontWeight: "bold" }}>{record.title}</p>
          <p style={{ color: "#ff4d4f" }}>此操作不可恢复</p>
        </div>
      ),
      icon: <InfoCircleOutlined style={{ color: "#ff4d4f" }} />,
      okText: "确认删除",
      okButtonProps: { danger: true },
      cancelText: "取消",
      onOk: async () => {
        try {
          const response = await request.delete(
            `${API_BASE_URL}/api/v3/knowledge/summaries/${record.id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (response.data.code === 200) {
            message.success("删除成功");
            // 刷新列表
            fetchKnowledgeList();
          } else {
            message.error(response.data.msg || "删除失败");
          }
        } catch (error) {
          message.error("删除失败，请重试");
        }
      },
    });
  };

  // 重新向量化(05§4.2 U3):索引失败/内容更新后手动重建该条目的向量
  const handleRevector = async (record: KnowledgeItem) => {
    if (revectoringId) return;
    setRevectoringId(record.id);
    try {
      const response = await request.post(
        `${API_BASE_URL}/api/v3/knowledge/summaries/${record.id}/revector`,
        undefined,
        { timeout: LONG_TASK_TIMEOUT }
      );
      if (response.data.code === 200) {
        message.success("已重新生成向量索引");
        fetchKnowledgeList();
      } else {
        message.error(response.data.msg || "重新向量化失败");
      }
    } catch (error: any) {
      if (error?.code === "ECONNABORTED") {
        message.warning("向量化仍在后台执行，请稍后刷新查看");
      } else {
        message.error(error?.response?.data?.msg || "重新向量化失败，请重试");
      }
    } finally {
      setRevectoringId(null);
    }
  };

  const checkVipAndShowModal = (
    modalSetter: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    // 检查用户是否是 PostChat 会员
    if (!user?.apiInfo?.postchat?.vip_info?.is_vip) {
      Modal.info({
        title: "会员功能",
        content: (
          <div>
            <p>此功能为全功能项目（Pro 及以上）专属能力</p>
            <p>升级后即可使用更多高级功能</p>
          </div>
        ),
        okText: "我知道了",
      });
      return;
    }
    modalSetter(true);
  };

  const addKnowledgeItems = [
    {
      key: "file",
      label: "上传文件",
      icon: <UploadOutlined />,
      onClick: () => checkVipAndShowModal(setIsUploadModalVisible),
    },
    {
      key: "text",
      label: "从文本添加",
      icon: <FileTextOutlined />,
      onClick: () => checkVipAndShowModal(setIsTextModalVisible),
    },
    {
      key: "web",
      label: "从网页添加",
      icon: <GlobalOutlined />,
      onClick: () => checkVipAndShowModal(setIsWebModalVisible),
    },
    {
      key: "code",
      label: "通过API添加",
      icon: <i className="ri-code-line" />,
      onClick: () => (window.location.href = "/console/code-generator"),
    },
  ];

  const columns: TableProps<KnowledgeItem>["columns"] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: "10%",
    },
    {
      title: "项目",
      dataIndex: "project_id",
      key: "project_id",
      width: "15%",
      render: (project_id: number) => {
        const project = projects.find((p) => p.id === project_id);
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <i
              className={
                project?.is_postchat ? "ri-message-3-line" : "ri-file-list-line"
              }
              style={{
                marginRight: 8,
                color: project?.is_postchat ? "#1890ff" : "#52c41a",
              }}
            />
            <span>{getProjectName(project_id)}</span>
          </div>
        );
      },
    },
    {
      title: "向量状态",
      dataIndex: "is_vector",
      key: "is_vector",
      width: "9%",
      render: (_, record) => {
        const isRevectoring = revectoringId === record.id;
        if (record.is_vector) {
          return (
            <Tooltip title="已建立向量索引,点击可重新生成(内容更新后建议重建)">
              <Tag
                icon={<i className="ri-checkbox-circle-line" />}
                color="success"
                style={{ cursor: isRevectoring ? "not-allowed" : "pointer", opacity: isRevectoring ? 0.6 : 1 }}
                onClick={() => !isRevectoring && handleRevector(record)}
              >
                {isRevectoring ? "重建中..." : "已索引"}
              </Tag>
            </Tooltip>
          );
        }
        return (
          <Tooltip title="尚未建立向量索引(生成中或失败),未索引的内容不会被对话检索到;点击重试">
            <Tag
              icon={<i className="ri-loader-4-line" />}
              color="warning"
              style={{ cursor: isRevectoring ? "not-allowed" : "pointer", opacity: isRevectoring ? 0.6 : 1 }}
              onClick={() => !isRevectoring && handleRevector(record)}
            >
              {isRevectoring ? "重建中..." : "未索引"}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "知识标题",
      dataIndex: "title",
      key: "title",
      width: "46%",
      ellipsis: {
        showTitle: false,
      },
      render: (title: string, record) => (
        <Tooltip title={title} placement="topLeft">
          <Space align="center">
            <TitleSiteFavicon record={record} />
            <Text
              strong
              style={{ cursor: "pointer" }}
              onClick={() => handleEdit(record)}
            >
              {title}
            </Text>
          </Space>
        </Tooltip>
      ),
    },
    {
      title: "播客状态",
      dataIndex: "podcast_status",
      key: "podcast_status",
      width: "10%",
      render: (_, record) => {
        if (!podcastConfig?.specified_mode) {
          return <Text type="secondary">-</Text>;
        }
        const isEnabled = summaryPodcastStatus.get(record.id) || false;
        const isToggling = togglingSummaryId === record.id;
        return isEnabled ? (
          <Tag
            icon={<i className="ri-mic-line" />}
            color="success"
            style={{
              cursor: isToggling ? "not-allowed" : "pointer",
              opacity: isToggling ? 0.6 : 1,
            }}
            onClick={() => !isToggling && handleTogglePodcastWhitelist(record.id)}
          >
            {isToggling ? "切换中..." : "可生成播客"}
          </Tag>
        ) : (
          <Tag
            icon={<i className="ri-mic-off-line" />}
            color="default"
            style={{
              cursor: isToggling ? "not-allowed" : "pointer",
              opacity: isToggling ? 0.6 : 1,
            }}
            onClick={() => !isToggling && handleTogglePodcastWhitelist(record.id)}
          >
            {isToggling ? "切换中..." : "不可生成"}
          </Tag>
        );
      },
    },
    {
      title: "生成时间",
      dataIndex: "created_at",
      key: "created_at",
      width: "10%",
      render: (time: string) => new Date(time).toLocaleString("zh-CN"),
    },
    {
      title: "操作",
      key: "action",
      width: "8%",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            onClick={() => handleEdit(record)}
            style={{ padding: 0 }}
          >
            编辑
          </Button>
          <Button
            type="link"
            onClick={() => handleDelete(record)}
            style={{ padding: 0 }}
            danger
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  // 自定义空状态组件
  const CustomEmpty = () => (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        <span>
          {selectedProjectId === "all"
            ? "暂无知识条目"
            : "该项目下暂无知识条目"}
        </span>
      }
    >
      <Dropdown
        menu={{ items: addKnowledgeItems }}
        placement="bottomRight"
        trigger={["hover"]}
      >
        <Button type="primary" icon={<PlusOutlined />}>
          添加知识
        </Button>
      </Dropdown>
    </Empty>
  );

  // 切换单个摘要的播客白名单状态
  const handleTogglePodcastWhitelist = async (summaryId: number) => {
    if (!podcastConfig) {
      message.error("请先配置播客功能");
      return;
    }

    if (!podcastConfig.specified_mode) {
      message.warning("请在播客配置中开启指定模式");
      return;
    }

    const currentStatus = summaryPodcastStatus.get(summaryId) || false;
    const newStatus = !currentStatus;

    setTogglingSummaryId(summaryId);
    try {
      const response = await request.patch(
        `${API_BASE_URL}/api/v3/podcasts/summary-switch`,
        {
          summary_id: summaryId,
          enabled: newStatus,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        message.success(
          newStatus
            ? "已加入播客白名单"
            : "已移出播客白名单"
        );
        // 更新本地状态
        setSummaryPodcastStatus((prev) => {
          const newMap = new Map(prev);
          newMap.set(summaryId, newStatus);
          return newMap;
        });
      } else {
        message.error(response.data.msg || "操作失败");
      }
    } catch (error: any) {
      console.error("切换播客白名单失败:", error);
      message.error(error.response?.data?.msg || "操作失败");
    } finally {
      setTogglingSummaryId(null);
    }
  };

  // 批量管理播客白名单
  const handleBatchPodcastWhitelist = async (action: "add" | "remove") => {
    if (selectedRowKeys.length === 0) {
      message.warning("请先选择要操作的摘要");
      return;
    }

    if (!podcastConfig) {
      message.error("请先配置播客功能");
      return;
    }

    if (!podcastConfig.specified_mode) {
      message.warning("请在播客配置中开启指定模式");
      return;
    }

    const selectedIds = selectedRowKeys.map((key) => Number(key));

    setPodcastWhitelistLoading(true);
    try {
      await Promise.all(
        selectedIds.map((summaryId) =>
          request.patch(
            `${API_BASE_URL}/api/v3/podcasts/summary-switch`,
            {
              summary_id: summaryId,
              enabled: action === "add",
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
        )
      );

      message.success(
        action === "add"
          ? `已将 ${selectedIds.length} 个摘要加入播客白名单`
          : `已将 ${selectedIds.length} 个摘要移出播客白名单`
      );
      // 重新获取当前页面所有摘要的播客状态
      if (knowledgeList.length > 0) {
        const allIds = knowledgeList.map((item) => item.id);
        await fetchSummaryPodcastStatus(allIds);
      }
      setSelectedRowKeys([]);
    } catch (error: any) {
      console.error("管理播客白名单失败:", error);
      message.error(error.response?.data?.msg || "操作失败");
    } finally {
      setPodcastWhitelistLoading(false);
    }
  };

  // 添加批量删除功能
  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning("请先选择要删除的知识条目");
      return;
    }

    Modal.confirm({
      title: "批量删除",
      content: (
        <div>
          <p>确定要删除选中的 {selectedRowKeys.length} 条知识条目吗？</p>
          <p style={{ color: "#ff4d4f" }}>此操作不可恢复</p>
        </div>
      ),
      icon: <InfoCircleOutlined style={{ color: "#ff4d4f" }} />,
      okText: "确认删除",
      okButtonProps: { danger: true },
      cancelText: "取消",
      onOk: async () => {
        setBatchLoading(true);
        try {
          // 批量删除单次调用(05§4.2 U4),后端返回成功/失败明细
          const response = await request.post(
            `${API_BASE_URL}/api/v3/knowledge/summaries-batch-delete`,
            { ids: selectedRowKeys.map((id) => Number(id)) }
          );
          if (response.data.code === 200) {
            const deleted = response.data.data?.deleted ?? 0;
            const failed: number[] = response.data.data?.failed ?? [];
            if (deleted > 0) {
              message.success(`成功删除 ${deleted} 条知识`);
            }
            if (failed.length > 0) {
              message.error(`有 ${failed.length} 条知识删除失败`);
            }
          } else {
            message.error(response.data.msg || "批量删除失败");
          }
          setSelectedRowKeys([]);
          fetchKnowledgeList();
        } catch (error) {
          message.error("批量删除过程中发生错误，请重试");
          console.error("批量删除错误:", error);
        } finally {
          setBatchLoading(false);
        }
      },
    });
  };

  // 选择行的配置
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    columnWidth: 32,
  };

  const sourceOptions = Array.from(
    new Set(
      knowledgeList
        .map((item) => item.refer || item.website || item.url || "")
        .filter(Boolean)
    )
  ).map((value) => ({ label: value, value }));

  return (
    <div
      style={{
        background: "#f0f2f5",
        minHeight: "100%",
        borderRadius: 8,
        padding: "16px",
      }}
    >
      <Card
        title={
          <Space>
            <i className="ri-book-line" style={{ color: "#1677ff" }}></i>
            知识库管理
          </Space>
        }
        extra={
          <Space>
            <Text type="secondary">共 {pagination.total} 条知识</Text>
          </Space>
        }
        variant={"borderless"}
        style={{ borderRadius: 8 }}
      >
        <Skeleton loading={initialLoading} active paragraph={{ rows: 6 }}>
          <div style={{ marginBottom: 16 }}>
            <Row gutter={[16, 16]} align="middle" justify="space-between">
              <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                <Space size="middle" wrap>
                  <Tag color="blue" style={{ height: 32, lineHeight: "30px", padding: "0 12px", fontSize: 14 }}>
                    {routeProject?.name || "—"}
                  </Tag>
                  <Input.Search
                    placeholder="搜索知识标题或内容"
                    style={{ width: 240 }}
                    value={searchText}
                    onChange={(e) => {
                      const next = e.target.value;
                      setSearchText(next);
                      // 用户清空输入框时，立即退出搜索态并回到正常列表
                      if (!next.trim() && searchedKeywordRef.current) {
                        setSearchedKeyword("");
                        void fetchKnowledgeList({
                          current: 1,
                          pageSize: paginationRef.current.pageSize,
                          keyword: "",
                        });
                      }
                    }}
                    onSearch={handleSearch}
                    loading={searchLoading}
                    enterButton
                    allowClear
                  />
                  <Select
                    style={{ width: 180 }}
                    placeholder="来源"
                    allowClear
                    showSearch
                    value={sourceFilter}
                    options={sourceOptions}
                    onChange={(value) => applyFilterChange({ source: value })}
                  />
                  <Select
                    style={{ width: 140 }}
                    placeholder="类型"
                    allowClear
                    value={docTypeFilter}
                    options={[
                      { label: "摘要", value: "summary" },
                      { label: "文件", value: "file" },
                      { label: "文本", value: "text" },
                    ]}
                    onChange={(value) => applyFilterChange({ docType: value })}
                  />
                  <Select
                    style={{ width: 140 }}
                    placeholder="向量状态"
                    allowClear
                    value={
                      typeof vectorFilter === "boolean"
                        ? String(vectorFilter)
                        : undefined
                    }
                    options={[
                      { label: "已索引", value: "true" },
                      { label: "未索引", value: "false" },
                    ]}
                    onChange={(value) =>
                      applyFilterChange({
                        vector: value === undefined ? undefined : value === "true",
                      })
                    }
                  />
                  <RangePicker
                    value={dateRangeFilter}
                    onChange={(range) => applyFilterChange({ dateRange: range })}
                  />
                  {(sourceFilter ||
                    docTypeFilter ||
                    typeof vectorFilter === "boolean" ||
                    dateRangeFilter) && (
                    <Button onClick={resetFilters}>重置筛选</Button>
                  )}
                </Space>
              </Col>
              <Col
                xs={24}
                sm={12}
                md={12}
                lg={12}
                xl={12}
                style={{ textAlign: "right" }}
              >
                <Dropdown
                  menu={{ items: addKnowledgeItems }}
                  placement="bottomRight"
                  trigger={["hover"]}
                >
                  <Button type="primary" icon={<PlusOutlined />}>
                    添加知识
                  </Button>
                </Dropdown>
              </Col>
            </Row>
          </div>

          <Table<KnowledgeItem>
            rowSelection={rowSelection}
            columns={columns}
            dataSource={knowledgeList}
            rowKey="id"
            loading={loading}
            pagination={false}
            onChange={handleTableChange}
            size="middle"
            locale={{
              emptyText: <CustomEmpty />,
            }}
            rowClassName={() => "knowledge-table-row"}
            footer={() => (
              <Row justify="space-between" align="middle">
                <Col>
                  {selectedRowKeys.length > 0 ? (
                    <Space>
                      <Text>已选择 {selectedRowKeys.length} 项</Text>
                      <Button
                        type="link"
                        onClick={() => setSelectedRowKeys([])}
                        style={{ padding: 0 }}
                      >
                        清除
                      </Button>
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={handleBatchDelete}
                        loading={batchLoading}
                      >
                        批量删除
                      </Button>
                      {podcastConfig?.specified_mode && (
                        <>
                          <Button
                            type="primary"
                            icon={<i className="ri-mic-line" />}
                            onClick={() => handleBatchPodcastWhitelist("add")}
                            loading={podcastWhitelistLoading}
                          >
                            加入播客白名单
                          </Button>
                          <Button
                            icon={<i className="ri-mic-off-line" />}
                            onClick={() =>
                              handleBatchPodcastWhitelist("remove")
                            }
                            loading={podcastWhitelistLoading}
                          >
                            移出播客白名单
                          </Button>
                        </>
                      )}
                    </Space>
                  ) : (
                    <div style={{ height: 32 }}></div> // 空占位以保持高度一致
                  )}
                </Col>
                <Col>
                  <Pagination
                    {...pagination}
                    showSizeChanger
                    showQuickJumper
                    showTotal={(total: number) => `共 ${total} 条`}
                    onChange={(page: number, pageSize: number) => {
                      handleTableChange({ current: page, pageSize });
                    }}
                    onShowSizeChange={(current: number, size: number) => {
                      handleTableChange({ current, pageSize: size });
                    }}
                  />
                </Col>
              </Row>
            )}
          />
        </Skeleton>
      </Card>

      {/* 使用提取的编辑知识组件 */}
      <KnowledgeEditModal
        visible={isEditModalVisible}
        onCancel={handleEditModalClose}
        currentRecord={currentRecord}
        onSuccess={() => {
          handleEditModalClose();
          fetchKnowledgeList();
        }}
        getProjectName={getProjectName}
      />

      {/* 使用提取的上传文件组件 */}
      <KnowledgeUploadModal
        visible={isUploadModalVisible}
        onCancel={() => setIsUploadModalVisible(false)}
        onSuccess={fetchKnowledgeList}
        projects={projects}
      />

      {/* 使用提取的文本添加组件 */}
      <KnowledgeTextModal
        visible={isTextModalVisible}
        onCancel={() => setIsTextModalVisible(false)}
        onSuccess={fetchKnowledgeList}
        projects={projects}
      />

      {/* 使用提取的网页添加组件 */}
      <KnowledgeWebModal
        visible={isWebModalVisible}
        onCancel={() => setIsWebModalVisible(false)}
        onSuccess={fetchKnowledgeList}
        projects={projects}
      />

      <style>
        {`
        .knowledge-table-row:hover {
          background-color: #f5f5ff;
        }
        
        /* 强制覆盖选择框列宽度 */
        .ant-table-selection-column {
          width: 32px !important;
          min-width: 32px !important;
          max-width: 32px !important;
        }
        
        /* 调整选择框内部间距 */
        .ant-checkbox-wrapper {
          padding: 0;
          margin-right: 0 !important;
        }
        `}
      </style>
    </div>
  );
};

export default Knowledge;
